import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Nav from '@/components/Nav';
import Footer from '@/components/Footer';
import StickyBottomMarquee, { STICKY_MARQUEE_SPACER_CLASS } from '@/components/StickyBottomMarquee';
import { api, fileUrl } from '@/lib/api';
import { MotionProvider, useReveal } from '@/lib/motion';
import MaintenancePage from '@/pages/MaintenancePage';

/*
  v3.13 — PartnerWall
  --------------------------------------------------------------
  A read-only page rendering ALL visible partners from the
  existing `partners` MongoDB collection (returned via
  GET /api/public/home). Per approved Option A of the diagnosis
  memo:

    • Reuses the existing partners records, logo R2 URLs, order,
      and visibility. NO backend / admin / MongoDB / R2 / global
      CSS / Tailwind changes.
    • The wall shows the FULL visible list — deliberately does
      not apply the homepage grid's 8-cap.
    • Page metadata (title, subhead, back label) is sourced from
      the existing admin-editable stub_routes.partners record so
      the heading stays admin-editable without introducing new
      fields anywhere.
    • Does NOT render the USA Today presenting-sponsor card on
      this page (per scope). The Footer still receives the same
      presenting-sponsor prop as Gallery.js because Footer itself
      already gates rendering on its own admin toggle.

  Animation is component-scoped via the local <style> block
  below (no global CSS edit). Motion respects prefers-reduced-
  motion via a `@media` guard.
*/

// Component-scoped stylesheet. Kept inside the file to honour the
// scope-lock instruction to avoid touching index.css / tailwind.config.
const WALL_STYLES = `
  .pw-tile { display: block; will-change: transform, opacity; }
  .pw-tile-inner {
    transition: transform 260ms cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 260ms ease-out;
  }
  /* Hover / focus lift is scoped to a.pw-tile on purpose — tiles
     rendered as div (unlinked partners, no valid URL) share the
     same visual chrome but must NOT invite interaction, since they
     are not navigable. */
  a.pw-tile:hover .pw-tile-inner,
  a.pw-tile:focus-visible .pw-tile-inner {
    transform: translateY(-4px);
    box-shadow: 0 14px 30px -14px rgba(15, 20, 30, 0.22);
  }
  .pw-logo {
    transition: filter 260ms ease-out, opacity 260ms ease-out;
  }
  a.pw-tile:hover .pw-logo,
  a.pw-tile:focus-visible .pw-logo {
    filter: grayscale(0);
    opacity: 1;
  }

  /* Idle float — subtle, staggered per-tile via inline animationDelay.
     Only runs when the user has NOT requested reduced motion. */
  @media (prefers-reduced-motion: no-preference) {
    @keyframes pw-float {
      0%, 100% { transform: translate3d(0, 0, 0); }
      50%      { transform: translate3d(0, -5px, 0); }
    }
    .pw-float {
      animation: pw-float 7s ease-in-out infinite;
      will-change: transform;
    }
  }

  /* Reduced-motion users: all decorative motion off. Hover lift
     is also removed since it would jump instead of transitioning. */
  @media (prefers-reduced-motion: reduce) {
    .pw-tile-inner, .pw-logo { transition: none !important; }
    .pw-float { animation: none !important; }
    a.pw-tile:hover .pw-tile-inner,
    a.pw-tile:focus-visible .pw-tile-inner {
      transform: none;
      box-shadow: 0 0 0 1px rgba(15, 20, 30, 0.12);
    }
  }
`;

export default function PartnerWall() {
  const [settings, setSettings] = useState({});
  const [bottomMarquee, setBottomMarquee] = useState({});
  const [wallMeta, setWallMeta] = useState({});
  const [partners, setPartners] = useState(null);
  const headerReveal = useReveal();
  const gridReveal = useReveal();

  useEffect(() => {
    api.get('/public/home').then((r) => {
      setSettings(r.data?.settings || {});
      setBottomMarquee(r.data?.bottom_marquee || {});
      setWallMeta(r.data?.stub_routes?.partners || {});
      // Defensive filter + sort even though the server already
      // returns visible-only, ordered ascending. This keeps the
      // page robust if the API contract ever changes.
      const list = (r.data?.partners || [])
        .filter((p) => p && p.visible !== false)
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setPartners(list);
    }).catch(() => setPartners([]));
  }, []);

  const maintenance = settings.maintenance || {};
  if (maintenance.enabled) return <MaintenancePage maintenance={maintenance} />;

  const motion = settings.motion || {};
  const nav = settings.nav || {};
  const footer = settings.footer || {};

  const pageTitle = (wallMeta.title || '').trim() || 'PARTNERS';
  const pageBody = (wallMeta.body || '').trim();
  const backLabel = (wallMeta.back_label || '').trim() || 'Back to Home';

  return (
    <MotionProvider motion={motion}>
      <style>{WALL_STYLES}</style>
      <div className={`bg-bg-canvas min-h-screen flex flex-col ${STICKY_MARQUEE_SPACER_CLASS}`}>
        <Nav nav={nav} motion={motion} />

        <div
          className="sticky top-[72px] z-30 bg-bg-canvas border-b border-ink-primary/10"
          data-testid="page-back-bar"
        >
          <div className="container-x py-3">
            <Link
              to="/"
              state={{ restoreScroll: true }}
              className="inline-flex items-center gap-2 text-ink-primary/70 hover:text-ink-primary font-body text-[13px] md:text-[14px] min-h-[44px] motion-safe:transition-colors motion-safe:duration-200 focus:outline-none focus-visible:underline"
              data-testid="page-back-link"
            >
              <ArrowLeft size={14} aria-hidden="true" /> {backLabel}
            </Link>
          </div>
        </div>

        <main className="flex-1" data-testid="page-partners">
          <section className="section-pad relative">
            <div className="container-x">
              <div
                ref={headerReveal.ref}
                className={`mt-2 text-center ${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}
              >
                <p className="eyebrow" data-testid="partners-page-eyebrow">POWERED BY PARTNERS.</p>
                <h1
                  className="mt-4 font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[40px] md:text-[56px] lg:text-[72px]"
                  data-testid="partners-page-headline"
                >
                  {pageTitle}
                </h1>
                <div className="mt-4 mx-auto w-16 h-[3px] bg-brand-orange" aria-hidden="true" />
                {pageBody && (
                  <p
                    className="mt-5 font-body text-[14px] md:text-[15px] text-ink-primary/70 max-w-[640px] mx-auto leading-relaxed"
                    data-testid="partners-page-subhead"
                  >
                    {pageBody}
                  </p>
                )}
              </div>

              <div ref={gridReveal.ref} className="mt-12">
                {/* Loading skeleton — matches the tile aspect ratio so the
                    layout does not shift when data resolves. */}
                {partners === null && (
                  <div
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4"
                    data-testid="partners-loading"
                  >
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div
                        key={i}
                        className="rounded-sm bg-ink-primary/10 animate-pulse"
                        style={{ aspectRatio: '3/2' }}
                      />
                    ))}
                  </div>
                )}

                {/* Empty state — no partners visible. Deliberately keeps
                    the page structure (header + orange bar) intact so
                    the layout does not collapse. */}
                {Array.isArray(partners) && partners.length === 0 && (
                  <p
                    className="font-body text-ink-muted text-center py-10"
                    data-testid="partners-empty"
                  >
                    No partners to display yet.
                  </p>
                )}

                {Array.isArray(partners) && partners.length > 0 && (
                  <div
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4"
                    data-testid="partners-page-grid"
                  >
                    {partners.map((p, i) => {
                      // Stagger the entrance reveal per tile but cap the
                      // delay so the last tile doesn't feel sluggish
                      // when the list is long (e.g. 27+ partners).
                      const staggerIdx = Math.min(i, 14);
                      // Spread float-animation phase across 0–4.9s so
                      // adjacent tiles are visibly out of sync.
                      const floatDelay = `${((i * 0.7) % 5).toFixed(2)}s`;

                      // A partner is only rendered as a clickable link
                      // when it has a real, non-placeholder URL. When
                      // `url` is missing / empty / whitespace / '#' we
                      // deliberately render the tile as an inert <div>
                      // so it cannot: (a) render `<a href="#">` (which
                      // would scroll the page to the top and pollute
                      // history), (b) receive a tab-stop / focus ring
                      // that invites keyboard activation, or (c) be
                      // announced as a link by assistive tech. The
                      // hover-lift CSS is scoped to `a.pw-tile` so
                      // static tiles also stay visually calm.
                      const trimmedUrl = typeof p.url === 'string' ? p.url.trim() : '';
                      const hasValidUrl = trimmedUrl.length > 0 && trimmedUrl !== '#';

                      const tileContent = (
                        <div
                          className="pw-float rounded-sm"
                          style={{ animationDelay: floatDelay }}
                        >
                          <div
                            className="pw-tile-inner bg-bg-elevated border border-border-line rounded-sm flex items-center justify-center px-4 py-4"
                            style={{ aspectRatio: '3/2' }}
                          >
                            {p.logo ? (
                              <img
                                src={fileUrl(p.logo)}
                                alt={p.name}
                                loading="lazy"
                                className="pw-logo max-h-[56px] md:max-h-[68px] lg:max-h-[76px] max-w-full object-contain grayscale opacity-70"
                              />
                            ) : (
                              <span className="font-display font-semibold uppercase text-ink-soft text-[12px] md:text-[13px] text-center tracking-wider2 leading-tight">
                                {p.name}
                              </span>
                            )}
                          </div>
                        </div>
                      );

                      const commonProps = {
                        className: `pw-tile rounded-sm ${gridReveal.simple ? 'reveal-simple' : 'reveal-card'} ${gridReveal.revealed ? 'is-in' : ''}`,
                        style: {
                          transitionDelay: gridReveal.revealed
                            ? `${staggerIdx * 55}ms`
                            : '0ms',
                        },
                        'data-testid': 'partners-page-tile',
                      };
                      const tileKey = p.id || i;

                      if (!hasValidUrl) {
                        // Inert visual chrome. No href, no tab-stop,
                        // no ARIA link semantics. The logo's alt text
                        // still provides accessible identification.
                        return (
                          <div
                            key={tileKey}
                            {...commonProps}
                            data-linked="false"
                          >
                            {tileContent}
                          </div>
                        );
                      }

                      return (
                        <a
                          key={tileKey}
                          {...commonProps}
                          className={`${commonProps.className} focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas`}
                          href={trimmedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={p.name}
                          data-linked="true"
                        >
                          {tileContent}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>

        <Footer
          footer={footer}
          motion={motion}
          presentingSponsor={settings.presenting_sponsor || null}
        />
        <StickyBottomMarquee bottomMarquee={bottomMarquee} />
      </div>
    </MotionProvider>
  );
}
